import { useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { enrollmentDeclined } from 'actions/enrollmentDeclined';
import { enrollmentPending } from 'actions/enrollmentPending';
import { enrollmentSuccessful } from 'actions/enrollmentSuccessful';

import { DefaultLoader } from 'components/Loaders/DefaultLoader';
import { buildChimeWebLink } from 'utils/linkHelper';
import doRedirect from 'utils/redirectHelper';
import responseTypes, { isValidCBRewardsResponse } from 'consts/responseConsts';
import {
  cbRewardsRoutes,
  DECLINE_ROUTE,
  PENDING_ROUTE,
  SUCCESS_ROUTE,
} from 'consts/routeConsts';

import { useMount } from 'hooks/useMount';

import { selectUserLoginToken } from 'selectors/user';

import { verifyScanIdSubmit } from 'thunks/verifyScanIdSubmit';

import { getQueryParamFromUrl } from 'utils/queryParamHelpers';
import { getValueFromStorage } from 'utils/storage/storageHelper';
import updateSuccessPageUrl from 'utils/successPageUrlHelper';

const VerifyScanIdPage = () => {
  const externalId = getQueryParamFromUrl('externalId');

  const history = useHistory();
  const dispatch = useDispatch();

  const loginToken = useSelector(selectUserLoginToken);
  const loginTokenRef = useRef();
  loginTokenRef.current = loginToken;

  async function makeVerifyScanIdRequest() {
    const cb_pre_order = getValueFromStorage('cb_pre_order');
    const safer_credit_building = getValueFromStorage('safer_credit_building');

    const {
      login_token: loginToken,
      email: userEmail,
      order_id: orderId,
      enrollment_status: enrollmentStatus,
      cash_rewards_enrollment_status: cashRewardsEnrollmentStatus,
      status,
      onboarding_flow,
    } = await dispatch(
      verifyScanIdSubmit(
        {
          scan_id_inquiry: externalId,
          cb_pre_order,
          safer_credit_building,
        },
        null,
        false
      )
    );

    // res.status is set to the status code in handleServerResponse.js
    if (enrollmentStatus === 'fail' || status !== 200) {
      dispatch(enrollmentDeclined());
      history.replace(DECLINE_ROUTE.path);
      return;
    }

    if (enrollmentStatus === 'pending_review') {
      dispatch(enrollmentPending());
      history.replace(PENDING_ROUTE.path);
      return;
    }

    if (isValidCBRewardsResponse(cashRewardsEnrollmentStatus)) {
      if (
        cashRewardsEnrollmentStatus ===
        responseTypes.SUCCESSFUL_REWARDS_ENROLLMENT
      ) {
        updateSuccessPageUrl(userEmail, loginTokenRef.current);
        history.push(cbRewardsRoutes.CB_REWARDS_SUCCESS);
      } else if (
        cashRewardsEnrollmentStatus ===
        responseTypes.INELIGIBLE_REWARDS_ENROLLMENT
      ) {
        updateSuccessPageUrl(userEmail, loginTokenRef.current);
        history.push(cbRewardsRoutes.CB_SUCCESS_NO_REWARDS);
      } else if (
        cashRewardsEnrollmentStatus === responseTypes.PENDING_REWARDS_ENROLLMENT
      ) {
        updateSuccessPageUrl(userEmail, loginTokenRef.current);
        history.push(cbRewardsRoutes.CB_PENDING_REWARDS_SUCCESS);
      }
      return;
    }

    dispatch(enrollmentSuccessful({ orderId, loginToken }));

    // If the user is coming from the mobile app, we need to add some special
    // values to the query string parameter before sending the user to the
    // success page, because the mobile app uses these values to login the user.
    // If the user is in web, clear out QSPs, because for Marketing tools, the success
    // page for web needs to have the same URL every time. https://stackoverflow.com/a/41061471
    updateSuccessPageUrl(userEmail, loginToken);
    if (onboarding_flow === 'tax_filing_2024_wedge_treatment') {
      const taxFilingEnrollRedirectLink = buildChimeWebLink(
        '/tax-filing/enroll?source=enrollment'
      );
      doRedirect(taxFilingEnrollRedirectLink, {});
    } else {
      history.replace(SUCCESS_ROUTE.path);
    }
  }

  useMount(() => {
    makeVerifyScanIdRequest();
  });

  // using loader internals because of IS_EMBEDDED issues
  return <DefaultLoader />;
};

export default VerifyScanIdPage;
